/**
 * 数据存储工具（使用 sessionStorage，关闭页面后自动清除）
 */
const STORAGE_KEY = 'power_forecast_data';
const CHAT_STORAGE_KEY = 'chat_messages';
const USER_STORAGE_KEY = 'auth_user';
const DEVICE_STORAGE_KEY = 'device_cache';
const ME_STORAGE_KEY = 'user_me';

export const storageUtil = {
  // 保存预测数据
  saveForecastData(data) {
    try {
      const dataToSave = {
        monthForecast: data.monthForecast || [],
        actualDaily: data.actualDaily || [],
        todayData: data.todayData || { actual: 0, predicted: 0 },
        historyData: data.historyData || [],
        timestamp: Date.now()
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
      return true;
    } catch (error) {
      console.error('保存数据失败:', error);
      return false;
    }
  },

  // 获取预测数据
  getForecastData() {
    try {
      const dataStr = sessionStorage.getItem(STORAGE_KEY);
      if (!dataStr) return null;
      
      const data = JSON.parse(dataStr);
      // 检查数据是否过期（可选：24小时过期）
      const now = Date.now();
      const expireTime = 24 * 60 * 60 * 1000; // 24小时
      if (now - data.timestamp > expireTime) {
        this.clearForecastData();
        return null;
      }
      return data;
    } catch (error) {
      console.error('读取数据失败:', error);
      return null;
    }
  },

  // 清除预测数据
  clearForecastData() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('清除数据失败:', error);
      return false;
    }
  },

  // 检查是否有数据
  hasForecastData() {
    return sessionStorage.getItem(STORAGE_KEY) !== null;
  },

  // ========== 聊天消息持久化（按用户ID隔离，登录时加载，登出时清除） ==========
  
  // 保存聊天消息（按用户ID隔离）
  saveChatMessages(messages, userId) {
    try {
      if (!userId) {
        console.warn('未提供用户ID，无法保存聊天消息');
        return false;
      }
      const storageKey = `${CHAT_STORAGE_KEY}_${userId}`;
      const dataToSave = {
        messages: messages || [],
        timestamp: Date.now(),
        userId: userId
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      return true;
    } catch (error) {
      console.error('保存聊天消息失败:', error);
      return false;
    }
  },

  // 获取聊天消息（按用户ID获取）
  getChatMessages(userId) {
    try {
      if (!userId) {
        console.warn('未提供用户ID，无法获取聊天消息');
        return null;
      }
      const storageKey = `${CHAT_STORAGE_KEY}_${userId}`;
      const dataStr = localStorage.getItem(storageKey);
      if (!dataStr) return null;
      
      const data = JSON.parse(dataStr);
      
      // 验证数据格式
      if (!data || typeof data !== 'object') {
        console.warn('聊天消息数据格式无效，清除存储');
        this.clearChatMessages(userId);
        return null;
      }
      
      // 验证用户ID是否匹配
      if (data.userId !== userId) {
        console.warn('用户ID不匹配，清除该聊天记录');
        this.clearChatMessages(userId);
        return null;
      }
      
      // 验证消息数组
      if (!Array.isArray(data.messages)) {
        console.warn('聊天消息不是数组格式，返回空数组');
        return [];
      }
      
      return data.messages;
    } catch (error) {
      console.error('读取聊天消息失败:', error);
      // 如果解析失败，清除无效数据
      try {
        const storageKey = `${CHAT_STORAGE_KEY}_${userId}`;
        localStorage.removeItem(storageKey);
      } catch (clearError) {
        console.error('清除无效聊天数据失败:', clearError);
      }
      return null;
    }
  },

  // 清除聊天消息（清除指定用户的聊天记录）
  clearChatMessages(userId) {
    try {
      if (!userId) {
        console.warn('未提供用户ID，无法清除聊天消息');
        return false;
      }
      const storageKey = `${CHAT_STORAGE_KEY}_${userId}`;
      localStorage.removeItem(storageKey);
      return true;
    } catch (error) {
      console.error('清除聊天消息失败:', error);
      return false;
    }
  },

  // 检查是否有聊天消息（检查指定用户）
  hasChatMessages(userId) {
    if (!userId) return false;
    const storageKey = `${CHAT_STORAGE_KEY}_${userId}`;
    return localStorage.getItem(storageKey) !== null;
  },

  // ========== 用户信息 ==========
  saveUser(user) {
    try {
      sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
      return true;
    } catch (error) {
      console.error('保存用户失败:', error);
      return false;
    }
  },

  getUser() {
    try {
      const dataStr = sessionStorage.getItem(USER_STORAGE_KEY);
      if (!dataStr) return null;
      
      const user = JSON.parse(dataStr);
      
      // 验证用户数据完整性
      if (!user || typeof user !== 'object') {
        console.warn('[Storage] 用户数据格式无效，清除存储');
        try {
          this.clearUser();
        } catch (clearError) {
          console.error('[Storage] 清除无效用户数据失败:', clearError);
        }
        return null;
      }
      
      // 验证必要字段
      if (!user.id || !user.role) {
        console.warn('[Storage] 用户数据不完整，清除存储', user);
        try {
          this.clearUser();
        } catch (clearError) {
          console.error('[Storage] 清除不完整用户数据失败:', clearError);
        }
        return null;
      }
      
      return user;
    } catch (error) {
      console.error('[Storage] 读取用户失败:', error);
      // 如果解析失败，清除无效数据
      try {
        sessionStorage.removeItem(USER_STORAGE_KEY);
      } catch (clearError) {
        console.error('[Storage] 清除无效用户数据失败:', clearError);
      }
      return null;
    }
  },

  clearUser() {
    try {
      sessionStorage.removeItem(USER_STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('清除用户失败:', error);
      return false;
    }
  },

  // ========== 设备缓存 ==========
  saveDeviceCache(data) {
    try {
      const payload = {
        devices: data.devices || [],
        types: data.types || [],
        timestamp: Date.now()
      };
      sessionStorage.setItem(DEVICE_STORAGE_KEY, JSON.stringify(payload));
      return true;
    } catch (error) {
      console.error('保存设备缓存失败:', error);
      return false;
    }
  },

  getDeviceCache() {
    try {
      const dataStr = sessionStorage.getItem(DEVICE_STORAGE_KEY);
      if (!dataStr) return null;
      const data = JSON.parse(dataStr);
      const now = Date.now();
      const expireTime = 10 * 60 * 1000;
      if (now - data.timestamp > expireTime) {
        this.clearDeviceCache();
        return null;
      }
      return data;
    } catch (error) {
      console.error('读取设备缓存失败:', error);
      return null;
    }
  },

  clearDeviceCache() {
    try {
      sessionStorage.removeItem(DEVICE_STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('清除设备缓存失败:', error);
      return false;
    }
  },

  // ========== 登录上下文缓存 ==========
  saveMeCache(data) {
    try {
      const payload = {
        data: data || null,
        timestamp: Date.now()
      };
      sessionStorage.setItem(ME_STORAGE_KEY, JSON.stringify(payload));
      return true;
    } catch (error) {
      console.error('保存登录上下文失败:', error);
      return false;
    }
  },

  getMeCache() {
    try {
      const dataStr = sessionStorage.getItem(ME_STORAGE_KEY);
      if (!dataStr) return null;
      const payload = JSON.parse(dataStr);
      const now = Date.now();
      const expireTime = 10 * 60 * 1000;
      if (now - payload.timestamp > expireTime) {
        this.clearMeCache();
        return null;
      }
      return payload.data || null;
    } catch (error) {
      console.error('读取登录上下文失败:', error);
      return null;
    }
  },

  clearMeCache() {
    try {
      sessionStorage.removeItem(ME_STORAGE_KEY);
      return true;
    } catch (error) {
      console.error('清除登录上下文失败:', error);
      return false;
    }
  }
};

